import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, Stack, Chip, Grid, Paper, Divider,
} from '@mui/material';
import CloseIcon       from '@mui/icons-material/Close';
import VisibilityIcon  from '@mui/icons-material/Visibility';
import { TABLE_CONFIG } from '../config/fieldConfig';
import { fetchRecords, fetchRecord } from '../services/api';

const getRowsFromResponse = (res) => {
  const payload = res?.data ?? res;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

/**
 * Read-only record viewer. Pairs with DataTable's onView callback.
 * Renders every field grouped by section (if table has sections) with FK labels resolved.
 */
export default function ViewRecordDialog({ open, onClose, schema, record: recordProp, tableName }) {
  const tableConfig = TABLE_CONFIG[tableName];
  const sections    = tableConfig?.sections;
  const fields      = tableConfig?.fields || {};
  const [fkLabels, setFkLabels] = useState({});
  const [record, setRecord] = useState(null);

  // Synchronize record with recordProp and fetch latest from backend
  useEffect(() => {
    if (open && recordProp) {
      setRecord(recordProp);
      // Fetch latest from backend
      fetchRecord(tableName, recordProp.id)
        .then(res => {
          if (res.data?.success && res.data?.data) {
            setRecord(res.data.data);
          }
        })
        .catch(err => {
          console.error('Error fetching record in ViewRecordDialog:', err);
        });
    } else if (!open) {
      setRecord(null);
    }
  }, [open, recordProp, tableName]);

  // Resolve FK display labels (similar to RecordForm's joinedData)
  useEffect(() => {
    if (!open || !record) return;
    const fkFields = Object.entries(fields).filter(([_, c]) => c?.type === 'dropdown' && c.source);
    const uniqueSources = [...new Set(fkFields.map(([_, c]) => c.source))];

    let cancelled = false;
    Promise.all(uniqueSources.map((src) =>
      fetchRecords(src).then((res) => [src, getRowsFromResponse(res)]).catch(() => [src, []])
    )).then((pairs) => {
      if (cancelled) return;
      const dataBySource = Object.fromEntries(pairs);
      const labels = {};
      for (const [fieldName, cfg] of fkFields) {
        const value = record[fieldName];
        if (value == null || value === '') continue;
        const src = dataBySource[cfg.source] || [];
        const row = src.find((r) => String(r[cfg.valueField]) === String(value));
        if (row) {
          if (cfg.labelCombo) {
            labels[fieldName] = cfg.labelCombo.map((k) => row[k]).filter(Boolean).join(' — ');
          } else {
            labels[fieldName] = row[cfg.labelField] || value;
          }
        }
      }
      setFkLabels(labels);
    });
    return () => { cancelled = true; };
  }, [open, record, tableName, fields]);

  if (!record) return null;

  const isImageValue = (raw) => {
    if (!raw || typeof raw !== 'string') return false;
    return raw.startsWith('data:image') || raw.startsWith('data:application') ||
           /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/.test(raw.slice(0, 64)) && raw.length > 200;
  };

  const getImageSrc = (raw) => {
    if (!raw) return null;
    if (raw.startsWith('data:')) return raw;
    return `data:image/png;base64,${raw}`;
  };

  const formatValue = (fieldName, cfg) => {
    const raw = record[fieldName];
    if (raw === null || raw === undefined || raw === '') return '—';
    if (cfg?.type === 'flag') return raw === 'Y' || raw === true ? 'Yes' : 'No';
    if (cfg?.type === 'date' && typeof raw === 'string') return raw;
    if (cfg?.type === 'dropdown' && fkLabels[fieldName]) return fkLabels[fieldName];
    if (cfg?.type === 'select') {
      const options = Array.isArray(cfg.options) ? cfg.options : [];
      const opt = options.find((o) => (o.value ?? o) === raw);
      if (opt) return opt.label ?? opt;
    }
    if (cfg?.type === 'image') return raw; // handled separately as image
    if (typeof raw === 'number') return raw.toLocaleString('en-IN');
    return String(raw);
  };

  // Group fields into sections (or single "All Fields" group if no sections defined)
  const groups = sections && sections.length
    ? sections.map((sec) => ({
        label: sec.label,
        fields: sec.fields.filter((f) => fields[f] && !f.startsWith('__')),
        columns: sec.columns || 2,
      }))
    : [{
        label: 'Record Details',
        fields: Object.keys(fields).filter((f) => fields[f]?.type !== 'auto'),
        columns: 2,
      }];

  const pk      = tableConfig?.pk;
  const pkValue = pk ? record[pk] : '';
  const label   = tableConfig?.label || tableName;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      <DialogTitle sx={{
        bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5, bgcolor: '#dbeafe',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <VisibilityIcon sx={{ fontSize: 20, color: '#2563eb' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>
              View {label}
            </Typography>
            {pkValue && (
              <Chip label={pkValue} size="small" sx={{
                mt: 0.4, height: 18, fontSize: 10.5, fontWeight: 700,
                bgcolor: '#eff6ff', color: '#2563eb',
              }} />
            )}
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: '#fafbfc' }}>
        {groups.map((grp, idx) => (
          <Paper key={idx} elevation={0} sx={{
            p: 2, mb: 1.5, borderRadius: 1.5, border: '1px solid #e5e7eb', bgcolor: 'white',
          }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{
              color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4,
              fontSize: 11, mb: 1.5,
            }}>
              {grp.label}
            </Typography>
            <Grid container spacing={2}>
              {grp.fields.map((fieldName) => {
                const cfg = fields[fieldName];
                const displayLabel = cfg?.label || fieldName;
                const raw = record[fieldName];
                const isImg = cfg?.type === 'image' || (raw && isImageValue(raw));
                const colWidth = 12 / (grp.columns || 2);

                if (isImg) {
                  const src = raw ? getImageSrc(raw) : null;
                  return (
                    <Grid item xs={12} sm={colWidth} key={fieldName}>
                      <Typography variant="caption" sx={{
                        color: '#64748b', fontWeight: 600, fontSize: 11,
                        textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 0.8,
                      }}>
                        {displayLabel}
                      </Typography>
                      {src ? (
                        <Box sx={{
                          border: '1.5px solid #e5e7eb', borderRadius: '10px',
                          overflow: 'hidden', display: 'inline-block',
                          maxWidth: 160, bgcolor: '#f8fafc',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                        }}>
                          <img
                            src={src}
                            alt={displayLabel}
                            style={{ display: 'block', width: '100%', maxWidth: 160, maxHeight: 160, objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display='none'; }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: 13 }}>No image</Typography>
                      )}
                    </Grid>
                  );
                }

                const value = formatValue(fieldName, cfg);
                const isEligibilityStatus = fieldName === 'eligibility_status';
                const eligibilityColor = raw === 'ELIGIBLE' ? '#15803d' : raw === 'NOT_ELIGIBLE' ? '#991b1b' : raw === 'CONDITIONAL' ? '#92400e' : '#111827';
                const eligibilityBg = raw === 'ELIGIBLE' ? '#dcfce7' : raw === 'NOT_ELIGIBLE' ? '#fef2f2' : raw === 'CONDITIONAL' ? '#fef3c7' : 'transparent';
                const eligibilityBorder = raw === 'ELIGIBLE' ? '#bbf7d0' : raw === 'NOT_ELIGIBLE' ? '#fecaca' : raw === 'CONDITIONAL' ? '#fde68a' : 'transparent';

                return (
                  <Grid item xs={12} sm={colWidth} key={fieldName}>
                    <Typography variant="caption" sx={{
                      color: '#64748b', fontWeight: 600, fontSize: 11,
                      textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 0.3,
                    }}>
                      {displayLabel}
                    </Typography>
                    {isEligibilityStatus ? (
                      <Chip label={value} size="small" sx={{
                        bgcolor: eligibilityBg, color: eligibilityColor, border: `1px solid ${eligibilityBorder}`,
                        fontWeight: 600, fontSize: 11.5, height: 22, borderRadius: '20px'
                      }} />
                    ) : (
                      <Typography variant="body2" sx={{
                        color: '#111827', fontWeight: 500, fontSize: 13.5,
                        wordBreak: 'break-word',
                      }}>
                        {value}
                      </Typography>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
